import { useEffect, useRef, useState } from "react";
import { NotificationItem, useNotifications } from "../api/notifications";
import { useAuth } from "../auth/AuthContext";
import { UI_LOADING } from "../constants/uiLabels";
import {
  notificationTypeToHeading,
  parseVehicleNotificationTitle,
  sanitizeVehicleNotificationMessage,
} from "../utils/vehicleAlertHeadings";
import "./NotificationsBell.css";

const SEVERITY_LABELS: Record<string, string> = {
  info: "Bilgi",
  warning: "Uyarı",
  critical: "Kritik",
  high: "Kritik",
  medium: "Orta",
};

function severityClass(severity: string): string {
  if (severity === "critical" || severity === "high") return "kp-bell__sev--critical";
  if (severity === "warning" || severity === "medium") return "kp-bell__sev--warning";
  return "kp-bell__sev--info";
}

function formatTime(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleString("tr-TR", { dateStyle: "short", timeStyle: "short" });
}

function displayTitle(item: NotificationItem): string {
  const fromType = notificationTypeToHeading(item.type);
  if (fromType) return `${parseVehicleNotificationTitle(item.title).plate} — ${fromType}`;
  return item.title;
}

function NotificationRow({
  item,
  isUnread,
  onMarkRead,
  onMarkUnread,
}: {
  item: NotificationItem;
  isUnread: boolean;
  onMarkRead: (id: string) => void;
  onMarkUnread: (id: string) => void;
}) {
  return (
    <li className={`kp-bell__item${isUnread ? " kp-bell__item--unread" : " kp-bell__item--read"}`}>
      <div className="kp-bell__item-head">
        <span className={`kp-bell__sev ${severityClass(item.severity)}`}>
          {SEVERITY_LABELS[item.severity] || item.severity}
        </span>
        <div className="kp-bell__item-head-end">
          <span className="kp-bell__time">{formatTime(item.created_at)}</span>
          {isUnread ? (
            <button
              type="button"
              className="kp-bell__read-btn"
              onClick={() => onMarkRead(item.id)}
              title="Okundu olarak işaretle"
              aria-label="Okundu olarak işaretle"
            >
              ✓
            </button>
          ) : (
            <button
              type="button"
              className="kp-bell__read-btn kp-bell__read-btn--marked"
              onClick={() => onMarkUnread(item.id)}
              title="Okunmadı olarak işaretle"
              aria-label="Okunmadı olarak işaretle"
            >
              ✓
            </button>
          )}
        </div>
      </div>
      <div className="kp-bell__title">{displayTitle(item)}</div>
      {item.message && (
        <div className="kp-bell__msg">{sanitizeVehicleNotificationMessage(item.message)}</div>
      )}
    </li>
  );
}

export function NotificationsBell() {
  const { session } = useAuth();
  const role = session?.user?.role;
  const enabled = role === "tenant_admin" || role === "manager" || role === "office_staff";
  const state = useNotifications(enabled);
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (event: MouseEvent) => {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const hasItems = state.open.length > 0;

  if (!enabled) return null;

  const badge =
    state.unreadCount > 99 ? "99+" : state.unreadCount > 0 ? String(state.unreadCount) : null;

  return (
    <div className="kp-bell" ref={containerRef}>
      <button
        type="button"
        className={`kp-bell__trigger${state.unreadCount > 0 ? " kp-bell__trigger--has-open" : ""}`}
        onClick={() => setOpen((prev) => !prev)}
        aria-haspopup="dialog"
        aria-expanded={open}
        title={state.unreadCount > 0 ? `${state.unreadCount} okunmamış bildirim` : "Bildirimler"}
      >
        <span className="kp-bell__icon" aria-hidden="true">
          &#128276;
        </span>
        {badge && <span className="kp-bell__badge">{badge}</span>}
      </button>

      {open && (
        <div className="kp-bell__panel kp-card" role="dialog">
          <div className="kp-bell__head">
            <strong>Bildirimler</strong>
            <button className="kp-btn kp-btn--ghost" onClick={state.reload} disabled={state.loading}>
              Yenile
            </button>
          </div>

          {state.loading && <div className="kp-loading">{UI_LOADING}</div>}
          {state.error && <div className="kp-error">{state.error}</div>}

          {!state.loading && !state.error && !hasItems && (
            <div className="kp-empty">Bildirim yok.</div>
          )}

          {!state.loading && !state.error && hasItems && (
            <div className="kp-bell__sections">
              {state.unread.length > 0 && (
                <section className="kp-bell__section">
                  <h3 className="kp-bell__section-title">Okunmamış ({state.unread.length})</h3>
                  <ul className="kp-bell__list">
                    {state.unread.map((item) => (
                      <NotificationRow
                        key={item.id}
                        item={item}
                        isUnread
                        onMarkRead={state.markAsRead}
                        onMarkUnread={state.markAsUnread}
                      />
                    ))}
                  </ul>
                </section>
              )}

              {state.read.length > 0 && (
                <section className="kp-bell__section">
                  <h3 className="kp-bell__section-title kp-bell__section-title--read">
                    Okundu ({state.read.length})
                  </h3>
                  <ul className="kp-bell__list">
                    {state.read.map((item) => (
                      <NotificationRow
                        key={item.id}
                        item={item}
                        isUnread={false}
                        onMarkRead={state.markAsRead}
                        onMarkUnread={state.markAsUnread}
                      />
                    ))}
                  </ul>
                </section>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
